<template>
	<div class="list-container column">
		<div class="title-container">
			<div class="title">{{ $t('roundchecklist.title') }}</div>
			<TabComponent
				class="tabs"
				:class="{ mobile: isMobile }"
				:type="'is-toggle'"
				:tabs="tabs"
				v-model="selectedTab"
			/>
			<div class="dash-select" :class="{ mobile: isMobile }">
				<v-select
					class="panel-select dashboard"
					:value="selectedChecklist"
					:options="checklists"
					:placeholder="$t('roundchecklist.select_placeholder')"
					:allowEmpty="false"
					:internalSearch="true"
					:showLabels="false"
					@select="selectChecklist"
					label="name"
					track-by="id"
				>
					<span slot="noResult">{{ $t('roundchecklist.no_results') }}</span>
					<span
						slot="caret"
						slot-scope="{ toggle }"
						class="caret"
						@mousedown.prevent.stop="toggle()"
					><i class="fas fa-caret-down caret-inner"></i
					></span>
				</v-select>
			</div>
		</div>
		<div
			class="category-container"
			v-for="(question, index) in comQuestions"
			:key="index"
		>
			<p
				class="category"
				:class="{ 'is-first': index === 0 }"
				v-if="
					question.groupName !==
						comQuestions[index > 0 ? index - 1 : index].groupName || index === 0
				"
			>
				{{ question.groupName }}
			</p>
			<div class="question-container">
				<div class="question-description" :class="{ mobile: isMobile }">
					<p>{{ question.name || question.description }}</p>
					<div class="lawlink" v-if="question.lawLink">
						{{ $t('roundchecklist.label_reference') }}:
						<a :href="getFormattedLawLink(question.lawLink)" target="_blank">{{
							question.lawLink
						}}</a>
					</div>
				</div>
				<div
					class="question-info"
					:class="{ mobile: isMobile }"
					v-if="question.description"
				>
					<p>{{ $t('roundchecklist.label_info') }}</p>
					<p>{{ question.description }}</p>
				</div>
				<div class="question-buttons" :class="{ mobile: isMobile }">
					<button
						class="button is-success"
						@click="positive(question)"
						:class="question.answer === question.positive ? 'active' : ''"
					>
						{{ question.positive }}
					</button>
					<button
						class="button is-danger"
						@click="toggleModal(question)"
						:class="question.answer === question.negative ? 'active' : ''"
					>
						{{ question.negative }}
					</button>
					<button
						class="button"
						:class="question.answer === '' ? 'is-link' : ''"
						@click="irrelevant(question)"
					>
						{{ $t('roundchecklist.button_irrelevant') }}
					</button>
				</div>
			</div>
		</div>
		<div class="panel-list no-hover" v-if="comQuestions.length == 0">
			<p class="panel-list-item">
				 {{ selectedTab ? $t('roundchecklist.no_answered_questions') : $t('roundchecklist.no_unanswered_questions') }}
			</p>
		</div>
		<QuestionModal
      v-if="showModal"
      :question="selectedQuestion"
      @save="saveNegative"
      @cancel="toggleModal"
    />
	</div>
</template>

<script>
import TabComponent from '@/components/TabComponent';
import QuestionModal from '../modals/QuestionModal';
import { mapGetters } from 'vuex';



export default {
  name: 'RoundChecklist',

  components: {
    TabComponent,
    QuestionModal
  },

  props: {
    checklists: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      selectedChecklist: null,
      selectedIndex: 0,
      selectedTab: 0,
      showModal: false,
      selectedQuestion: {}
    };
  },

  computed: {
    ...mapGetters('appStore', ['isMobile']),
    ...mapGetters('user', ['getUser']),
    ...mapGetters('roundHistoryStore', ['activeRound']),

    _locale() {
      return this.$i18n ? this.$i18n.locale : undefined;
    },

    tabs() {
      this._locale;

      const t = (k, p) => (this.$t ? this.$t(k, p) : k);
      return [
        { name: t('roundchecklist.tab_unanswered'), icon: 'fa-clipboard-list' },
        { name: t('roundchecklist.tab_answered'),   icon: 'fa-clipboard-check' }
      ];
    },

    comQuestions() {
      const list = this.selectedChecklist &&
                   Array.isArray(this.selectedChecklist.roundHistoryQuestions)
        ? [...this.selectedChecklist.roundHistoryQuestions] // copy before sorting
        : [];

      list.sort((a, b) => {
        if (a.groupName > b.groupName) return 1;
        if (a.groupName < b.groupName) return -1;
        return a.id > b.id ? 1 : -1;
      });

      if (this.selectedTab === 0) {
        return list.filter(q => q.answer === null);
      }
      return list.filter(q => q.answer !== null);
    }
  },

  watch: {
    checklists: {
      immediate: true,
      handler(newLists) {
        if (!Array.isArray(newLists) || newLists.length === 0) {
          this.selectedChecklist = null;
          this.selectedIndex = 0;
          return;
        }
        if (this.selectedIndex < 0 || this.selectedIndex >= newLists.length) {
          this.selectedIndex = 0;
        }
        this.selectedChecklist = newLists[this.selectedIndex];
      }
    }
  },

  methods: {

    createChecklistFlaw(roundData, question, modalPayload) {
      const infoIdx = roundData?.currentInfo ?? 0;
      const info = roundData?.infos?.[infoIdx];
      if (!info) return;

      info.flaws = Array.isArray(info.flaws) ? info.flaws : [];

      const now = new Date();

      info.flaws.push({
        id: null,
        name: modalPayload?.name || question.name || question.description || question.negative,
        description: modalPayload?.description || '',
        created: now,
        done: null,
        doneBy: null,

        source: 'checklist',
        checklistQuestionId: question.id,

        flawScale: modalPayload?.flawScale ?? null,
        file: modalPayload?.file ?? null,
        flawPositionX: modalPayload?.flawPositionX ?? null,
        flawPositionY: modalPayload?.flawPositionY ?? null,
      });
    },


    deepClone(obj) { return JSON.parse(JSON.stringify(obj)); },

    applyAnswer(question, answerValue) {
      const roundData = this.deepClone(this.activeRound);
      if (!roundData?.checklists) return;

      const checklistId = this.selectedChecklist?.id;
      const clIdx = roundData.checklists.findIndex(c => c?.id === checklistId);
      if (clIdx === -1) return;

      const qIdx = (roundData.checklists[clIdx].roundHistoryQuestions || [])
        .findIndex(q => q?.id === question?.id);
      if (qIdx === -1) return;

      const updatedQ = { ...roundData.checklists[clIdx].roundHistoryQuestions[qIdx] };
      updatedQ.answer = answerValue;
      updatedQ.userName = this.getUser.email;

      updatedQ.answerDescription = updatedQ.answerDescription || '';
      updatedQ.done = updatedQ.done || null;

      roundData.checklists[clIdx].roundHistoryQuestions[qIdx] = updatedQ;

      this.selectedChecklist = roundData.checklists[clIdx];

      this.$emit('saveState', roundData);
    },


    selectChecklist(list) {
      const idx = this.checklists.findIndex(el => el && el.id === list.id);
      this.selectedIndex = idx === -1 ? 0 : idx;
      this.selectedChecklist = this.checklists[this.selectedIndex] || null;
    },

    positive(question) {
      this.applyAnswer(question, question.positive);
    },

    irrelevant(question) {
      this.applyAnswer(question, '');
    },

    saveNegative() {
      const q = this.selectedQuestion;
      if (!q?.id) return;

      const roundData = this.deepClone(this.activeRound);
      if (!roundData?.checklists?.length) return;

      const checklistId = this.selectedChecklist?.id;
      const clIdx = roundData.checklists.findIndex(c => c?.id === checklistId);
      if (clIdx === -1) return;

      const questions = roundData.checklists[clIdx].roundHistoryQuestions || [];
      const qIdx = questions.findIndex(x => x?.id === q.id);
      if (qIdx === -1) return;

      const updatedQ = { ...questions[qIdx] };
      updatedQ.answer = updatedQ.negative;
      updatedQ.userName = this.getUser.email;

      updatedQ.answerDescription = q.answerDescription || '';
      updatedQ.done = q.done || null;

      questions[qIdx] = updatedQ;

      this.selectedChecklist = roundData.checklists[clIdx];

      this.toggleModal();

      this.$emit('saveState', roundData);
    },

    toggleModal(question) {
      if (question) this.selectedQuestion = question;
      this.showModal = !this.showModal;
      console.log('toggleModal', { showModal: this.showModal, q: this.selectedQuestion?.id });
    },


    getFormattedLawLink(lawLink) {
      if (!lawLink) return '#';
      const s = String(lawLink);
      if (s.toUpperCase().startsWith('HTTP')) return s;
      return `https://${s}`;
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/colors.scss";
.title-container {
  display: flex;
  flex-wrap: wrap;
}
.dash-select {
  margin-top: 0.05rem;
  margin-top: -0.5rem;
  &.mobile {
    margin-right: 0.5rem;
    margin-left: auto;
    margin-top: -0.5rem;
    margin-bottom: 0.5rem;
  }
}
.lawlink {
  margin-top: 1rem;
  word-break: break-word;
}

p.category {
  border-top: 1px solid #d3d3d35d;
  &.is-first {
    border: none;
  }
  font-weight: 600;
}
.category-container {
  border-bottom: 1px solid #d3d3d35d;
  &:last-child {
    border: none;
  }
}
.question-container {
  padding: 0.75rem;
  display: flex;
  flex-wrap: wrap;
  .question-info {
    margin-left: auto;
  }
  .question-description,
  .question-info {
    max-width: 50%;
    min-width: 50%;
    margin-bottom: 0.5rem;
    &.mobile {
      padding: 10px 0;
      min-width: 100%;
      max-width: 100%;
    }
    a {
      color: #4a87d8;
    }
  }
  .question-description {
    p {
      font-weight: 500;
    }
  }
  .question-info {
    font-size: 15px;
  }
  .question-buttons {
    width: 100%;
    display: flex;
    justify-content: flex-end;
    &.mobile {
      justify-content: space-between;
    }
    .button {
      min-width: 80px;
      margin: 0 0.25rem;
      &.is-success {
        background-color: #02aa3d;
      }
      &:not(.active) {
        &.is-success {
          background-color: #7eca99;
        }
        &.is-danger {
          background-color: #e98787;
        }
      }
      &:first-child {
        margin-left: 0;
      }
      &:last-child {
        margin-right: 0;
      }
    }
  }
}

.tabs {
  margin-left: auto;
  margin-right: 0.5rem;
  margin-top: -0.55rem;
  &.mobile {
    margin-top: -0.5rem;
  }
}
</style>

<style lang="scss">
.multiselect.panel-select.dashboard {
  min-width: 220px;
  max-width: 220px;
  .caret {
    background-color: #fcfcfc;
  }
  .multiselect__tags {
    background-color: white;

    .multiselect__single,
    .multiselect__input {
      background-color: white;
    }
  }
  .multiselect__content {
    background: white;
  }
}
</style>
